import React, { useCallback, useEffect, useMemo, useState, } from 'react';
import { Animated, Easing, StyleSheet, View } from 'react-native';
import { AppView } from 'design-system/AppView';
import { rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, } from 'design-system/values';
import StatusBar from 'design-system/StatusBar';
import { CollapsableContainer } from 'design-system/CollapsableContainer';
import Button from 'design-system/Button';
import Text from 'design-system/Text';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import IconRepeat from 'design-system/icons/IconRepeat';
import { useAppSelector } from 'store/hooks';
import useColors from 'hooks/useColors';
import useForceSync from 'hooks/useForceSync';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { selectIsSyncingManual } from 'reducers/selectors';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { isWeb } from '../../../constants';
const strings = {
    waitForSyncing: 'Waiting for syncing',
    waitForSyncingDescription: 'The payment hasn’t been detected yet because syncing is still in progress. It should appear soon.',
    waitForSyncingButton: 'Sync now',
    renewBankAccess: 'Bank access needs to be renewed',
    renewBankAccessDescription: 'Emma may not detect the payment because your bank connection has stopped syncing.',
    referenceChange: 'Transaction reference has changed',
    referenceChangeDescription: 'If the merchant changed the transaction reference, Emma might not recognise the payment as committed spending. If you find the transaction in the Emma app, you can add it to the detected recurring payment. This will update your committed spending.',
    referenceChangeButton: 'Check recent transactions',
};
/**
 * The "waiting for syncing" reason, with a refresh icon that spins while a
 * manual sync is running.
 */
export const useSyncReasonItem = () => {
    const colors = useColors();
    const isSyncingManual = useAppSelector(selectIsSyncingManual);
    const [isFetchingSyncStatus, forceSync] = useForceSync();
    const isSpinning = isFetchingSyncStatus || isSyncingManual;
    const [{ spinValue, spinAnim, rotate }] = useState(() => {
        const value = new Animated.Value(0);
        return {
            spinValue: value,
            spinAnim: Animated.timing(value, {
                toValue: 1,
                duration: 1000,
                easing: Easing.linear,
                useNativeDriver: true,
                isInteraction: false,
            }),
            rotate: value.interpolate({
                inputRange: [0, 1],
                outputRange: ['0deg', '360deg'],
            }),
        };
    });
    const spin = useCallback(() => {
        spinValue.setValue(0);
        // stop() delivers finished: false, so the loop ends there.
        spinAnim.start(({ finished }) => {
            if (finished) {
                spin();
            }
        });
    }, [spinAnim, spinValue]);
    useEffect(() => {
        if (!isSpinning) {
            spinValue.setValue(0);
            spinAnim.reset();
            return undefined;
        }
        spin();
        return () => spinAnim.stop();
    }, [isSpinning, spin, spinAnim, spinValue]);
    return useMemo(() => ({
        title: strings.waitForSyncing,
        description: strings.waitForSyncingDescription,
        buttonText: strings.waitForSyncingButton,
        icon: (<Animated.View style={[styles.iconLeft, { transform: [{ rotate }] }]}>
          <IconRepeat width={16} height={16} color={colors.text.textOnBrand}/>
        </Animated.View>),
        onPress: () => forceSync(),
    }), [colors.text.textOnBrand, forceSync, rotate]);
};
/**
 * The "renew bank access" reason. Returns null on web, where the bank
 * connections screen isn't available.
 */
export const useBankAccessReasonItem = (buttonText) => {
    const navigation = useMainStackNavigation();
    return useMemo(() => {
        if (isWeb) {
            return null;
        }
        return {
            title: strings.renewBankAccess,
            description: strings.renewBankAccessDescription,
            buttonText,
            onPress: () => navigation.navigateDeprecated('Logins'),
        };
    }, [buttonText, navigation]);
};
export const useReferenceChangeReasonItem = () => {
    const navigation = useMainStackNavigation();
    return useMemo(() => ({
        title: strings.referenceChange,
        description: strings.referenceChangeDescription,
        buttonText: strings.referenceChangeButton,
        onPress: () => navigation.navigateDeprecated('TransactionsList'),
    }), [navigation]);
};
const ReasonsScreenContent = ({ title, subtitle, items, collapsable = false, withBottomPadding = false, onScroll, }) => {
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottomStyle = useMarginBottom('paddingBottom', withBottomPadding ? SCREEN_BOTTOM_PADDING : undefined, paddingHorizontalStyle);
    return (<AppView type="secondary" withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <Animated.ScrollView onScroll={onScroll} scrollEventThrottle={16} contentContainerStyle={paddingBottomStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
        <ScreenInfoHeader title={title} subTitle={subtitle} style={styles.header}/>
        <View style={styles.containersWrapper}>
          {items.map((item, index) => (<CollapsableContainer title={item.title} key={item.title} collapsable={collapsable} isCollapsedByDefault={collapsable} style={index > 0 ? styles.containerMargin : null}>
              <View style={styles.collapsibleContent}>
                <View style={styles.descriptionContainer}>
                  <Text TextThin-14 Secondary>
                    {item.description}
                  </Text>
                </View>
                <Button title={item.buttonText} brandLight square onPress={item.onPress} leftIcon={item.icon}/>
              </View>
            </CollapsableContainer>))}
        </View>
      </Animated.ScrollView>
    </AppView>);
};
const styles = StyleSheet.create({
    header: {
        marginTop: rem(16),
        paddingBottom: rem(16),
    },
    containersWrapper: {
        paddingHorizontal: rem(16),
    },
    containerMargin: {
        marginTop: rem(16),
    },
    collapsibleContent: {
        paddingTop: rem(8),
        paddingHorizontal: rem(16),
        paddingBottom: rem(16),
    },
    descriptionContainer: {
        marginBottom: rem(16),
    },
    iconLeft: {
        marginRight: -4,
    },
});
export default ReasonsScreenContent;
